import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Shield, Mail, RefreshCw, Loader2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

type AuthState = 
  | "needs_verification"
  | "sending_otp"
  | "awaiting_code"
  | "verifying";

interface AdminVerificationFormProps {
  authState: AuthState;
  code: string;
  setCode: (code: string) => void;
  error: string | null;
  attempts: number;
  maxAttempts: number;
  resendCooldown: number;
  ttlHours: number;
  onVerify: () => void;
  onResend: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
}

export const AdminVerificationForm: React.FC<AdminVerificationFormProps> = ({
  authState,
  code,
  setCode,
  error,
  attempts,
  maxAttempts,
  resendCooldown,
  ttlHours,
  onVerify,
  onResend,
  onKeyDown,
}) => {
  const isSending = authState === "sending_otp" || authState === "needs_verification";
  const isVerifying = authState === "verifying";
  const canVerify = code.length === 6 && !isVerifying;
  const canResend = resendCooldown === 0 && !isSending;

  return (
    <main className="min-h-screen flex items-center justify-center p-6 bg-background">
      <Card className="max-w-md w-full">
        <CardHeader className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-primary/10">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Admin Verification</CardTitle>
              <CardDescription>Two-factor authentication required</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status message */}
          <div className="flex items-center gap-2 p-3 rounded-lg bg-muted">
            <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
            <p className="text-sm text-muted-foreground">
              {isSending 
                ? "Sending verification code to your email..."
                : "Enter the 6-digit code sent to your admin email."
              }
            </p>
          </div>

          {/* Error message */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Code input */}
          <div className="space-y-2">
            <label htmlFor="otp-code" className="text-sm font-medium">
              Verification Code
            </label>
            <Input
              id="otp-code"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={onKeyDown}
              disabled={isSending || isVerifying}
              aria-label="6-digit verification code"
              className="text-center text-2xl tracking-[0.5em] font-mono h-14"
              autoComplete="one-time-code"
              autoFocus
            />
          </div>

          {/* Verify button */}
          <Button 
            onClick={onVerify} 
            disabled={!canVerify}
            className="w-full"
            size="lg"
          >
            {isVerifying ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              "Verify & Continue"
            )}
          </Button>

          {/* Attempts warning */}
          {attempts > 0 && (
            <p className="text-xs text-center text-amber-600 dark:text-amber-400">
              {maxAttempts - attempts} verification attempt{maxAttempts - attempts !== 1 ? 's' : ''} remaining
            </p>
          )}

          {/* Resend section */}
          <div className="flex items-center justify-center pt-2 border-t">
            <button 
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed" 
              onClick={onResend} 
              disabled={!canResend}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isSending ? 'animate-spin' : ''}`} />
              {isSending 
                ? "Sending..." 
                : resendCooldown > 0 
                  ? `Resend code in ${resendCooldown}s` 
                  : "Resend code"
              }
            </button>
          </div>

          {/* Session info */}
          <p className="text-xs text-center text-muted-foreground">
            Your session will remain active for {ttlHours} hours after verification.
          </p>
        </CardContent>
      </Card>
    </main>
  );
};
